/**
 * Citation Badge - Inline Citation Indicator
 *
 * A compact, color-coded badge that appears inline in AI responses.
 * Shows verification type, confidence, and triggers hover card on hover.
 */

import React, { useState, useRef, useEffect } from 'react';

import { CheckCircle2, FileText, Calculator, Link2, AlertCircle, ChevronRight } from 'lucide-react';

import { CitationHoverCard } from '@/components/RichChat/citations/CitationHoverCard';

// Badge configuration by verification type
const BADGE_CONFIG = {
  direct_quote: {
    bgClass:
      'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50',
    textClass: 'text-emerald-700 dark:text-emerald-300',
    borderClass: 'border-emerald-200 dark:border-emerald-800',
    icon: CheckCircle2,
    shortLabel: 'V', // Verbatim
  },
  paraphrase: {
    bgClass: 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50',
    textClass: 'text-blue-700 dark:text-blue-300',
    borderClass: 'border-blue-200 dark:border-blue-800',
    icon: FileText,
    shortLabel: 'S', // Summarized
  },
  calculation: {
    bgClass: 'bg-violet-100 hover:bg-violet-200 dark:bg-violet-900/30 dark:hover:bg-violet-900/50',
    textClass: 'text-violet-700 dark:text-violet-300',
    borderClass: 'border-violet-200 dark:border-violet-800',
    icon: Calculator,
    shortLabel: 'C', // Calculated
  },
  composite: {
    bgClass: 'bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50',
    textClass: 'text-amber-700 dark:text-amber-300',
    borderClass: 'border-amber-200 dark:border-amber-800',
    icon: Link2,
    shortLabel: 'X', // Cross-file
  },
  inference: {
    bgClass: 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50',
    textClass: 'text-orange-700 dark:text-orange-300',
    borderClass: 'border-orange-200 dark:border-orange-800',
    icon: AlertCircle,
    shortLabel: 'I', // Inferred
  },
};

/**
 * Citation Badge Component
 *
 * @param {Object} props
 * @param {Object} props.citation - The citation object
 * @param {number} props.index - Citation index (for display number)
 * @param {Function} props.onClick - Click handler
 * @param {Function} props.onGoToSource - Handler for "Go to Source"
 * @param {boolean} props.showHoverCard - Whether to show hover card
 * @param {string} props.variant - 'inline' | 'compact' | 'detailed'
 */
export function CitationBadge({
  citation,
  index = 0,
  onClick,
  onGoToSource,
  showHoverCard = true,
  variant = 'inline',
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false); // ✅ Click-to-pin state
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const cardRef = useRef(null);
  const hoverTimeout = useRef(null);

  // ✅ Click outside OR press ESC to close pinned card
  useEffect(() => {
    if (!isPinned) return;

    const handleClickOutside = e => {
      // Check if click is outside both badge and card
      const isOutsideBadge = badgeRef.current && !badgeRef.current.contains(e.target);
      const isOutsideCard = cardRef.current && !cardRef.current.contains(e.target);

      if (isOutsideBadge && isOutsideCard) {
        setIsPinned(false);
        setIsHovered(false);
      }
    };

    // ✅ ESC key to close
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        setIsPinned(false);
        setIsHovered(false);
      }
    };

    // Add listeners with slight delay to avoid immediate close
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPinned]);

  // Get configuration
  const verificationType = citation?.verification?.type || citation?.display?.type || 'inference';
  const config = BADGE_CONFIG[verificationType] || BADGE_CONFIG.inference;
  const IconComponent = config.icon;

  // Get display info
  const displayNumber = index + 1;
  const fileName =
    citation?.sourceFile?.originalName ||
    citation?.sourceFile?.name ||
    citation?.display?.fileName ||
    'Source';
  const shortFileName = fileName.length > 20 ? fileName.substring(0, 17) + '...' : fileName;
  const locationString =
    citation?.display?.locationString || buildLocationString(citation?.location) || '';

  // ═══════════════════════════════════════════════════════════════════════
  // ✅ WAVE 25 FIX: Robust confidence calculation - handles both 0-1 and 0-100 formats
  // ═══════════════════════════════════════════════════════════════════════
  const rawConfidence =
    citation?.display?.confidence ??
    citation?.verification?.confidenceScore ??
    citation?.verification?.confidence ??
    0.7;
  // If value > 1, it's already a percentage; if <= 1, multiply by 100
  const normalizedConfidence = rawConfidence > 1 ? rawConfidence : Math.round(rawConfidence * 100);
  const confidence = Math.min(100, Math.max(0, normalizedConfidence)); // Cap at 100%

  // Helper to build location string from location object
  function buildLocationString(location) {
    if (!location) return '';
    const parts = [];
    if (location.pageNumber) parts.push(`Page ${location.pageNumber}`);
    if (location.sheetName) parts.push(location.sheetName);
    if (location.cellCoordinates) parts.push(`Cell ${location.cellCoordinates}`);
    if (location.rowNumber) parts.push(`Row ${location.rowNumber}`);
    if (location.slideNumber) parts.push(`Slide ${location.slideNumber}`);
    return parts.join(' • ');
  }

  // Handle mouse enter with delay - ✅ Only for hover preview, not when pinned
  const handleMouseEnter = () => {
    if (!showHoverCard || isPinned) return; // Don't change anything if already pinned

    hoverTimeout.current = setTimeout(() => {
      if (badgeRef.current && !isPinned) {
        const rect = badgeRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        // Calculate position (prefer above, fall back to below)
        let top = rect.top - 10; // Above the badge
        let left = rect.left + rect.width / 2;

        // Adjust if too close to edges
        if (left < 200) left = 200;
        if (left > viewportWidth - 200) left = viewportWidth - 200;
        if (top < 300) top = rect.bottom + 10; // Show below if not enough space above

        setHoverPosition({ top, left });
        setIsHovered(true);
      }
    }, 300); // 300ms delay before showing
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    // ✅ NEVER hide if pinned - only hide hover preview
    if (isPinned) return;

    // Add delay before hiding so user can move mouse to hover card
    hoverTimeout.current = setTimeout(() => {
      if (!isPinned) setIsHovered(false);
    }, 200); // 200ms grace period to move to hover card
  };

  // ✅ Keep hover card visible when mouse enters it
  const handleHoverCardEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    // Only set hovered if not already pinned
    if (!isPinned) setIsHovered(true);
  };

  const handleHoverCardLeave = () => {
    // ✅ NEVER hide if pinned
    if (isPinned) return;
    setIsHovered(false);
  };

  // ✅ Check if card should be visible (hovered OR pinned)
  const isCardVisible = (isHovered || isPinned) && showHoverCard;

  // Handle click - ✅ Toggle pin state (does NOT call parent onClick - that's for "View Source")
  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    // Clear any pending hover timeouts
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }

    // Calculate position for pinned card
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      let top = rect.top - 10;
      let left = rect.left + rect.width / 2;

      if (left < 200) left = 200;
      if (left > viewportWidth - 200) left = viewportWidth - 200;
      if (top < 300) top = rect.bottom + 10;

      setHoverPosition({ top, left });
    }

    // Toggle pinned state - if already pinned, unpin; if not pinned, pin
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    setIsHovered(newPinned); // Keep visible when pinning

    // ✅ DO NOT call parent onClick here - clicking just pins/unpins the card
    // Parent onClick is called by "View Source" button in hover card
  };

  // Render based on variant
  if (variant === 'compact') {
    return (
      <button
        ref={badgeRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium
          border ${config.bgClass} ${config.textClass} ${config.borderClass}
          transition-colors cursor-pointer select-none
        `}
        title={`${fileName} - ${locationString} (${confidence}%)`}
      >
        <span className="font-semibold">[{displayNumber}]</span>
      </button>
    );
  }

  if (variant === 'detailed') {
    return (
      <div
        ref={badgeRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg
          border ${config.bgClass} ${config.textClass} ${config.borderClass}
          transition-colors cursor-pointer group
        `}
      >
        <IconComponent className="h-4 w-4 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{shortFileName}</div>
          <div className="text-[10px] opacity-70 truncate">{locationString}</div>
        </div>
        <div className="text-xs font-semibold">{confidence}%</div>
        <ChevronRight className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />

        {/* Hover Card Portal - ✅ Shows on hover OR when pinned */}
        {isCardVisible && (
          <div
            ref={cardRef}
            className={`fixed z-50 transform -translate-x-1/2 -translate-y-full ${isPinned ? 'ring-2 ring-blue-400 ring-offset-2 rounded-xl' : ''}`}
            style={{ top: hoverPosition.top, left: hoverPosition.left }}
            onMouseEnter={handleHoverCardEnter}
            onMouseLeave={handleHoverCardLeave}
          >
            <CitationHoverCard
              citation={citation}
              onGoToSource={onGoToSource}
              isPinned={isPinned}
              onClose={() => {
                setIsPinned(false);
                setIsHovered(false);
              }}
            />
          </div>
        )}
      </div>
    );
  }

  // Default inline variant
  return (
    <>
      <button
        ref={badgeRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium
          border ${config.bgClass} ${config.textClass} ${config.borderClass}
          transition-colors cursor-pointer select-none align-baseline mx-0.5
          ${isPinned ? 'ring-2 ring-blue-400' : ''}
        `}
        title={`${fileName} - ${locationString} (${confidence}%) - Click to pin`}
      >
        <IconComponent className="h-3 w-3 flex-shrink-0" />
        <span className="font-semibold">S{displayNumber}</span>
        {locationString && (
          <>
            <span className="opacity-50">•</span>
            <span className="opacity-70 max-w-[80px] truncate">{locationString}</span>
          </>
        )}
      </button>

      {/* Hover Card Portal - ✅ Shows on hover OR when pinned */}
      {isCardVisible && (
        <div
          ref={cardRef}
          className={`fixed z-50 transform -translate-x-1/2 -translate-y-full ${isPinned ? 'ring-2 ring-blue-400 ring-offset-2 rounded-xl' : ''}`}
          style={{ top: hoverPosition.top, left: hoverPosition.left }}
          onMouseEnter={handleHoverCardEnter}
          onMouseLeave={handleHoverCardLeave}
        >
          <CitationHoverCard
            citation={citation}
            onGoToSource={onGoToSource}
            isPinned={isPinned}
            onClose={() => {
              setIsPinned(false);
              setIsHovered(false);
            }}
          />
        </div>
      )}
    </>
  );
}

/**
 * Citation Badge List - For displaying multiple citations
 */
export function CitationBadgeList({
  citations = [],
  onClick,
  onGoToSource,
  maxVisible = 5,
  showHoverCard = true,
}) {
  const [showAll, setShowAll] = useState(false);

  const visibleCitations = showAll ? citations : citations.slice(0, maxVisible);
  const hiddenCount = citations.length - maxVisible;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visibleCitations.map((citation, index) => (
        <CitationBadge
          key={citation.citationId || citation.citation_id || index}
          citation={citation}
          index={index}
          onClick={onClick}
          onGoToSource={onGoToSource}
          showHoverCard={showHoverCard}
          variant="inline"
        />
      ))}

      {!showAll && hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 font-medium"
        >
          +{hiddenCount} more
        </button>
      )}

      {showAll && hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(false)}
          className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 font-medium"
        >
          Show less
        </button>
      )}
    </div>
  );
}

export default CitationBadge;
